import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import {
  arrayToMap,
  removeFromMap,
  SpinnerV2Service,
  StorageV2Service,
  StoreV2,
} from '@schenck/rotec-components';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  skip,
  switchMap,
  take,
  takeUntil,
  concatMap,
  finalize,
} from 'rxjs/operators';
import { AverageRotor, RotorApi, RotorForComparison } from '../api';
import { SettingsService } from './settings.service';
import { showParameter } from 'src/utils/helpers';
import { unitDefaults } from 'src/utils/constants/settings.const';
import { Parameter, Parameters } from 'src/utils/interfaces/settings.interface';

export interface RotorCompareState {
  /**
   * Selected Rotor-Ids.
   */
  selected: string[];
  /**
   * A record of rotors.
   */
  rotors: Record<string, RotorForComparison>;
  /**
   * Per machine type a average rotor, if any.
   */
  average: AverageRotor | undefined;
}

export interface ParameterConfig {
  /**
   * The maximal number of measurements from all the selected rotors.
   */
  noOfMeasurements: number;
  /**
   * The amount of runs that should be shown.
   */
  noOfRuns: number;
  /**
   * The planes number of planes that should be shown.
   */
  noOfPlanes: number[];
  /**
   * The maximal number of corrections from all selected rotors.
   */
  noOfCorrections: number;
  /**
   * The maximal number of actions from all selected rotors.
   */
  noOfActions: number;
}

@Injectable()
export class RotorCompareService extends StoreV2<RotorCompareState> implements OnDestroy {
  get parameters() {
    return this.settingsService.state.parameters;
  }

  parameters$ = this.settingsService.settings$.pipe(map((settings) => settings.parameters));
  parameterConfig$ = this.computeParameterConfig$();

  public reloadRotorSubscription$: Subscription;

  private rotorChange$ = new Subject();
  private reloadRotors$ = new Subject();
  private destroy$ = new Subject();

  constructor(
    private rotorApi: RotorApi,
    private settingsService: SettingsService,
    private loadingService: SpinnerV2Service,
    storageService: StorageV2Service
  ) {
    super(
      {
        selected: [],
        rotors: {},
        average: undefined,
      },
      'RotorCompareService',
      { name: 'rotorCompareService', storageService }
    );
  }

  /**
   * Loads rotors from the backend.
   *
   * Reload data about selected rotors, even if they are already loaded from localStorage.
   * Reason: There might have been new measurements in the meantime.
   */
  public loadRotors() {
    this.computeAverage();
    this.listenToReloadAllRotors();
    this.reloadRotorsOnDecimalsChange();
    this.reloadRotorsOnParametersChange();
    this.reloadRotorsOnUnitsChange();
    this.reloadRotors$.next(null);
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.reloadRotorSubscription$?.unsubscribe();
  }

  computeParameterConfig$(): Observable<ParameterConfig> {
    return combineLatest([this.state$, this.parameters$]).pipe(
      map(([compareState, parameters]) => {
        const measurements = compareState.selected
          .map((rotorId) => compareState.rotors[rotorId])
          .filter((rotor) => !!rotor)
          .map((rotor) => rotor.measurements);

        const parameterConfig: ParameterConfig = {
          noOfMeasurements: 0,
          noOfRuns: 0,
          noOfPlanes: [],
          noOfCorrections: 0,
          noOfActions: 0,
        };

        let maxNoOfRuns = 0;
        let maxNoOfPlanes = 0;
        let maxNoOfCorrections = 0;

        measurements.forEach((_measurements) => {
          parameterConfig.noOfMeasurements = Math.max(
            ~~_measurements.length,
            parameterConfig.noOfMeasurements
          );
          _measurements.forEach((measurement) => {
            maxNoOfRuns = Math.max(~~measurement.result.runs.length, maxNoOfRuns);
            measurement.result.runs.forEach((run) => {
              maxNoOfPlanes = Math.max(~~run.unbal.dynamic.length, maxNoOfPlanes);
              maxNoOfCorrections = Math.max(~~run.correction.length, maxNoOfCorrections);
              run.correction.forEach((correction) => {
                parameterConfig.noOfActions = Math.max(
                  ~~correction.actions.length,
                  parameterConfig.noOfActions
                );
              });
            });
          });
        });

        this.computeNoOfRuns(parameterConfig, parameters, maxNoOfRuns);
        this.computeNoOfPlanes(parameterConfig, parameters, maxNoOfPlanes);
        this.computeNoOfCorrections(parameterConfig, parameters, maxNoOfCorrections);
        return parameterConfig;
      })
    );
  }

  private computeNoOfRuns(
    parameterConfig: ParameterConfig,
    parameters: Parameters | Parameter,
    maxNoOfRuns: number
  ): void {
    if (showParameter(parameters, 'allRuns')) {
      parameterConfig.noOfRuns = maxNoOfRuns;
    } else {
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Unary_plus
      parameterConfig.noOfRuns =
        +showParameter(parameters, 'firstRun') + +showParameter(parameters, 'lastRun');
    }
  }

  private computeNoOfPlanes(
    parameterConfig: ParameterConfig,
    parameters: Parameters | Parameter,
    maxNoOfPlanes: number
  ): void {
    if (showParameter(parameters, 'allPlanes')) {
      //=> [1, 2, 3, 4, 5, 6, ..., maxNoOfPlanes]
      parameterConfig.noOfPlanes = Array.from({ length: maxNoOfPlanes }, (_, i) => i + 1);
    } else {
      if (showParameter(parameters, 'plane1')) {
        parameterConfig.noOfPlanes.push(1);
      }

      if (showParameter(parameters, 'plane2')) {
        parameterConfig.noOfPlanes.push(2);
      }
    }
  }

  private computeNoOfCorrections(
    parameterConfig: ParameterConfig,
    parameters: Parameters | Parameter,
    maxNoOfCorrections: number
  ): void {
    parameterConfig.noOfCorrections =
      showParameter(parameters, 'method') || showParameter(parameters, 'values')
        ? maxNoOfCorrections
        : 0;
  }

  private reloadRotorsOnDecimalsChange() {
    this.settingsService.settings$
      .pipe(
        map((settings) => settings.unitSettings?.decimals),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.reloadRotors$.next(null));
  }

  private reloadRotorsOnParametersChange() {
    this.settingsService.settings$
      .pipe(
        map((settings) => settings.parameters),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.reloadRotors$.next(null));
  }

  private reloadRotorsOnUnitsChange() {
    this.settingsService.settings$
      .pipe(
        map((settings) => settings.unitSettings?.units),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.reloadRotors$.next(null));
  }

  private computeAverage() {
    combineLatest([this.rotorChange$, this.settingsService.settings$])
      .pipe(
        // Debounce to prevent spam of average
        debounceTime(1000),
        filter(([_, settings]) => settings.showAverage),
        switchMap(([_, settings]) => {
          if (this.state.selected.length === 0) {
            return of<AverageRotor>();
          }
          return this.rotorApi
            .average(
              this.state.selected,
              settings.unitSettings?.decimals,
              settings.unitSettings?.units || unitDefaults
            )
            .pipe(catchError(() => of<AverageRotor>()));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((average) => this.patchState('Update Average', { average }));
  }

  /**
   * Changes the rotor position.
   */
  moveRotor(previousPosition: number, newPosition: number) {
    const selected = [...this.state.selected];
    moveItemInArray(selected, previousPosition, newPosition);
    this.patchState('move rotor', {
      selected,
    });
  }

  /**
   * Adds a new rotor to the comparison. Requests its measurements from the backend.
   */
  addRotor(id: string) {
    const selected = [id, ...this.state.selected];
    this.patchState('add rotor', { selected });
  }

  /**
   * Requests and returns information about single rotor from backend
   */
  getRotor$(id: string): Observable<RotorForComparison> {
    return combineLatest([this.getDecimals$(), this.getUnits$()]).pipe(
      switchMap(([decimals, units]) => this.rotorApi.get(id, decimals, units))
    );
  }

  private listenToReloadAllRotors() {
    this.reloadRotorSubscription$ = this.reloadRotors$
      .pipe(
        debounceTime(500),
        concatMap(() => {
          this.rotorChange$.next(null);
          this.updateLoading(1);
          return combineLatest([this.getDecimals$(), this.getUnits$()]).pipe(
            switchMap(([decimals, units]) => {
              if (this.state.selected.length === 0) {
                // Return early here because if combineLatest is called with empty array,
                // it will never return
                return of([]);
              }
              return combineLatest(
                this.state.selected.map((rotorId) =>
                  this.rotorApi
                    .get(rotorId, decimals, units ? units : unitDefaults)
                    // If reload fails, use old state
                    .pipe(catchError(() => of(this.state.rotors[rotorId])))
                )
              );
            })
          );
        })
      )
      .subscribe((rotors: any) => {
        this.patchState('reloaded all rotors', {
          rotors: arrayToMap(rotors, 'id'),
        });
        this.updateLoading(-1);
      });
  }

  /**
   * Removes a rotor from the comparison.
   */
  removeRotor(id: string) {
    const rotors = removeFromMap(this.state.rotors, id);
    const selected = this.state.selected.filter((_id) => _id !== id);

    this.patchState('remove rotor', { rotors, selected });
    this.rotorChange$.next(null);
  }

  /**
   * Removes all rotors.
   */
  clearAll() {
    this.patchState('clear all', { rotors: {}, selected: [] });
  }

  toggleRotorSelect(id: string, select: boolean) {
    const selected = this.state.selected.filter((_id) => _id !== id);
    if (select) {
      selected.push(id);
    }

    this.patchState('toggle rotor select', { selected });
  }

  /**
   * Returns decimals. Waits until they are loaded.
   */
  private getDecimals$() {
    return this.settingsService.settings$.pipe(
      take(1),
      map((settings) => settings.unitSettings?.decimals)
    );
  }

  /**
   * Returns units. Waits until they are loaded.
   */
  private getUnits$() {
    return this.settingsService.settings$.pipe(
      take(1),
      map((settings) => settings.unitSettings?.units)
    );
  }

  private updateLoading(loading: number) {
    this.loadingService.updateLoading('compare', loading);
  }
}
